import { useMemo, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Info,
  ListChecks,
  LogIn,
  LogOut,
  Network,
  PanelLeftClose,
  Plus,
  Search,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Wordmark } from "./Wordmark";
import { PoweredByPee } from "./PoweredByPee";
import { AboutPeeModal } from "./AboutPeeModal";
import { SYSTEM_DESCRIPTOR } from "@/lib/looseend/pee";

import { groupByRecency } from "@/lib/looseend/format";
import { useSignOut } from "@/lib/auth/useSignOut";
import { useAppState } from "@/lib/looseend/store";
import { useHostedLoops } from "@/lib/loops/product/hostedLoops";
import { useHostedConversations } from "@/lib/chat/product/hostedChat";
import { analytics } from "@/lib/analytics";
import { queueLoops } from "@/lib/looseend/selectors";
import { cn } from "@/lib/utils";

/**
 * Progressive disclosure — power navigation appears once the data that makes
 * it useful exists (authoritative hosted reads, never local state):
 *   - Search once conversation history exists;
 *   - Resolution Queue / Chaos Map once at least one Loop exists.
 * The routes themselves always exist and remain directly reachable.
 */
const NAV = [
  { to: "/app/search", label: "Search", icon: Search, requires: "history" },
  { to: "/app/queue", label: "Resolution Queue", icon: ListChecks, requires: "loops" },
  { to: "/app/chaos-map", label: "Chaos Map", icon: Network, requires: "loops" },
] as const;

export function Sidebar({
  onNavigate,
  onNewConversation,
  onCollapse,
}: {
  onNavigate?: () => void;
  onNewConversation: () => void;
  onCollapse?: () => void;
}) {
  const state = useAppState();
  const signOut = useSignOut();
  const [about, setAbout] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // WP-B — conversation history is an authoritative hosted read, never local state.
  const conversations = useHostedConversations();
  const groups = useMemo(
    () => groupByRecency(conversations.conversations.filter((c) => !c.archivedAt)),
    [conversations.conversations],
  );
  const hosted = useHostedLoops();
  const pending = queueLoops([...hosted.loops]).length;
  const hasHistory = groups.length > 0;
  const hasLoops = hosted.loops.length > 0;
  const visibleNav = NAV.filter((item) =>
    item.requires === "loops" ? hasLoops : hasHistory || hasLoops,
  );
  const isGuest = state.user?.isGuest === true;
  const name = isGuest ? "Guest" : state.user?.name || "You";

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface">
      <div className="flex items-start gap-2 px-4 py-3.5">
        <div className="min-w-0">
          <Link to="/app" onClick={onNavigate} className="block min-w-0">
            <span className="sr-only">L-Infinity-se-End, Personal Execution System</span>
            <Wordmark className="text-[17px]" aria-hidden="true" />
            <span
              aria-hidden="true"
              className="block text-[11.5px] font-medium tracking-[0.01em] text-foreground/80"
            >
              {SYSTEM_DESCRIPTOR}
            </span>
          </Link>
          <PoweredByPee className="mt-0.5 block" />
        </div>
        {onCollapse ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Collapse sidebar"
            className="ml-auto size-8 text-muted-foreground"
            onClick={onCollapse}
          >
            <PanelLeftClose className="size-[18px]" aria-hidden="true" />
          </Button>
        ) : null}
      </div>

      <div className="px-3">
        <Button
          className="h-9 w-full justify-start rounded-[10px]"
          onClick={() => {
            onNewConversation();
            onNavigate?.();
          }}
        >
          <Plus className="size-4" aria-hidden="true" />
          New conversation
        </Button>
      </div>

      {visibleNav.length > 0 ? (
        <nav className="mt-3 px-3" aria-label="Primary">
          <ul className="space-y-0.5">
            {visibleNav.map((item) => {
              const active = pathname.startsWith(item.to);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onNavigate}
                    className={cn(
                      "flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm transition-colors",
                      active
                        ? "bg-card font-medium text-foreground"
                        : "text-muted-foreground hover:bg-card/70",
                    )}
                  >
                    <item.icon className="size-4 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 truncate">{item.label}</span>
                    {item.to === "/app/queue" && pending > 0 ? (
                      <span className="ml-auto rounded-full bg-primary-soft px-1.5 py-0.5 text-xs font-medium text-primary">
                        {pending}
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}

      <div className="mt-5 min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          History
        </p>
        {isGuest ? (
          <p className="px-3 pb-2 text-xs text-muted-foreground" data-testid="sidebar-guest-note">
            Kept for this browser session.{" "}
            <Link
              to="/auth"
              search={{ redirect: pathname }}
              onClick={onNavigate}
              className="font-medium text-foreground underline-offset-2 hover:underline"
            >
              Sign in to save your history
            </Link>
          </p>
        ) : null}
        {conversations.phase === "loading" ? (
          <div className="space-y-1.5 px-3 pt-1" aria-busy="true">
            <div className="h-7 w-full animate-pulse rounded-[10px] bg-card" />
            <div className="h-7 w-4/5 animate-pulse rounded-[10px] bg-card" />
          </div>
        ) : conversations.phase === "unavailable" ? (
          <div className="px-3 pt-1">
            <p className="text-sm text-muted-foreground">{conversations.message}</p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 h-7 px-2 text-xs"
              onClick={conversations.refresh}
            >
              Try again
            </Button>
          </div>
        ) : groups.length === 0 ? (
          <p className="px-3 pt-1 text-sm text-muted-foreground">No conversations yet.</p>
        ) : (
          groups.map((group) => (
            <div key={group.label} className="mt-3 first:mt-1">
              <p className="px-3 py-1 text-xs text-muted-foreground/80">{group.label}</p>
              <ul className="mt-0.5 space-y-0.5">
                {group.items.map((conversation) => {
                  const active = pathname === `/app/chat/${conversation.id}`;
                  return (
                    <li key={conversation.id}>
                      <Link
                        to="/app/chat/$conversationId"
                        params={{ conversationId: conversation.id }}
                        onClick={onNavigate}
                        className={cn(
                          "block truncate rounded-[10px] px-3 py-2 text-sm transition-colors",
                          active
                            ? "bg-card font-medium text-foreground"
                            : "text-foreground/80 hover:bg-card/70",
                        )}
                      >
                        {conversation.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>

      <div className="border-t border-border px-3 py-2.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-[10px] px-2 py-1.5 text-left transition-colors hover:bg-card"
              data-testid="sidebar-account-control"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
                {name.slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">{name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {isGuest ? "Sign in to save your history" : (state.user?.email ?? "")}
                </span>
              </span>
              <Settings className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {isGuest ? (
              <DropdownMenuItem asChild>
                <Link to="/auth" search={{ redirect: pathname }} onClick={onNavigate}>
                  <LogIn className="size-4" aria-hidden="true" />
                  Sign in to save history
                </Link>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem asChild>
              <Link to="/app/settings" onClick={onNavigate}>
                <Settings className="size-4" aria-hidden="true" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAbout(true)}>
              <Info className="size-4" aria-hidden="true" />
              About L∞seEnd™
            </DropdownMenuItem>

            {isGuest ? null : (
              <DropdownMenuItem onClick={() => void signOut()}>
                <LogOut className="size-4" aria-hidden="true" />
                Sign out
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AboutPeeModal open={about} onOpenChange={setAbout} />
    </div>
  );
}
